import { Button as UiButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Hash, Link2, Search, X } from 'lucide-react'
import { NeedSource, SourceContext } from '@Purchasing/Domain/Model/PurchaseProcess'
import { useCreatePurchaseProcess } from '@Purchasing/Hooks/useCreatePurchaseProcess/useCreatePurchaseProcess'
import { useSourceContext } from '@Purchasing/Hooks/useSourceContext/useSourceContext'
import { RoutesPaths } from '@Purchasing/RoutesPaths'
import { formatRouteId } from '@Utils/Formatters/formatRouteId'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

const manualSchema = z.object({
    title: z.string().min(3, 'Informe um título com pelo menos 3 caracteres'),
    description: z.string().min(3, 'Descreva o que precisa ser comprado'),
    quantity: z
        .number({ invalid_type_error: 'Informe a quantidade' })
        .int('Quantidade deve ser um número inteiro')
        .positive('Quantidade deve ser maior que zero'),
    notes: z.string().optional(),
})

type ManualValues = z.infer<typeof manualSchema>

const fieldLabel = 'mb-1 block text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground'
const fieldError = 'mt-1 block text-xs text-od-secondary-600'

/**
 * Processo manual: item que não existe no Brinde.me (ex.: produto ainda não
 * cadastrado), fornecedor com tabela de preço ou compra de estoque. O
 * orçamento do Brinde.me é opcional e serve só para vincular cliente/empresa.
 */
export const ManualProcessForm = ({ assignedTo }: { assignedTo?: string } = {}) => {
    const navigate = useNavigate()
    const { create, loading: creating } = useCreatePurchaseProcess()
    const { loadSourceContext, loading: loadingContext, error: contextError } = useSourceContext()
    const [budgetCodeInput, setBudgetCodeInput] = useState('')
    const [linked, setLinked] = useState<{ code: number; context: SourceContext } | null>(null)

    const { register, handleSubmit, formState: { errors } } = useForm<ManualValues>({
        resolver: zodResolver(manualSchema),
        defaultValues: { quantity: 1 },
    })

    const handleLinkBudget = async () => {
        const code = Number(budgetCodeInput)
        if (!code) return
        const context = await loadSourceContext(String(code))
        setLinked(context ? { code, context } : null)
    }

    const onSubmit = handleSubmit(async (values) => {
        const result = await create({
            ...(linked ? { budgetCode: linked.code } : {}),
            ...(assignedTo ? { assignedTo } : {}),
            title: values.title,
            need: {
                source: NeedSource.MANUAL,
                description: values.description,
                quantity: values.quantity,
                notes: values.notes,
            },
        })
        if (result) navigate(formatRouteId(RoutesPaths.PurchasingDetail, result._id))
    })

    const budget = linked?.context.budget

    return (
        <div className="flex flex-col gap-4">
            <section className="rounded-card border border-border p-4 md:p-5">
                <h2 className="mb-1 font-display text-base font-bold">Vincular a um orçamento (opcional)</h2>
                <p className="mb-3 text-sm text-muted-foreground">
                    Se existir orçamento no Brinde.me, vincule para trazer cliente e empresa. Os itens você informa abaixo.
                </p>
                {linked ? (
                    <div className="flex flex-wrap items-center gap-3 rounded-md bg-secondary px-4 py-3 text-sm" data-testid="manual-linked-budget">
                        <Link2 className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                        <span className="font-semibold">#{budget?.code ?? linked.code}</span>
                        <span className="truncate">{budget?.customer?.name ?? '-'}</span>
                        <span className="truncate text-muted-foreground">{budget?.company?.name ?? ''}</span>
                        <UiButton type="button" size="sm" variant="ghost" className="ml-auto gap-1" onClick={() => { setLinked(null); setBudgetCodeInput('') }}>
                            <X className="h-4 w-4" strokeWidth={1.5} />
                            Remover vínculo
                        </UiButton>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-end gap-2">
                        <label className="block">
                            <span className={fieldLabel}>Código do orçamento</span>
                            <div className="relative">
                                <Hash className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                                <Input
                                    aria-label="Código do orçamento (opcional)"
                                    inputMode="numeric"
                                    placeholder="Ex.: 2489"
                                    className="h-10 w-48 pl-9"
                                    value={budgetCodeInput}
                                    onChange={(e) => setBudgetCodeInput(e.target.value.replace(/\D/g, ''))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLinkBudget() } }}
                                />
                            </div>
                        </label>
                        <UiButton type="button" variant="outline" className="h-10 gap-2" disabled={loadingContext || !budgetCodeInput} onClick={handleLinkBudget}>
                            <Search className="h-4 w-4" strokeWidth={1.5} />
                            {loadingContext ? 'Buscando…' : 'Vincular'}
                        </UiButton>
                    </div>
                )}
                {contextError && !linked && (
                    <p className="mt-3 rounded-r-md border-l-[3px] border-l-brand-pink bg-od-secondary-50 px-3 py-2 text-sm text-od-secondary-600" role="alert">
                        {contextError}
                    </p>
                )}
            </section>

            <section className="rounded-card border border-border p-4 md:p-5">
                <h2 className="mb-3 font-display text-base font-bold">O que precisa ser comprado?</h2>
                <form onSubmit={onSubmit} className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_160px]" data-testid="manual-process-form">
                    <label className="block">
                        <span className={fieldLabel}>Título do processo</span>
                        <Input aria-label="Título do processo" placeholder="Ex.: Halter personalizado" {...register('title')} />
                        {errors.title && <span className={fieldError}>{errors.title.message}</span>}
                    </label>
                    <label className="block">
                        <span className={fieldLabel}>Quantidade</span>
                        <Input aria-label="Quantidade" type="number" min={1} step={1} {...register('quantity', { valueAsNumber: true })} />
                        {errors.quantity && <span className={fieldError}>{errors.quantity.message}</span>}
                    </label>
                    <label className="block md:col-span-2">
                        <span className={fieldLabel}>Descrição do item</span>
                        <Textarea aria-label="Descrição da necessidade" rows={4} placeholder="Material, medidas, cores, personalização…" {...register('description')} />
                        {errors.description && <span className={fieldError}>{errors.description.message}</span>}
                    </label>
                    <label className="block md:col-span-2">
                        <span className={fieldLabel}>Observações (opcional)</span>
                        <Textarea aria-label="Observações" rows={2} {...register('notes')} />
                    </label>
                    <div className="flex flex-wrap gap-2 md:col-span-2">
                        <UiButton type="submit" disabled={creating}>{creating ? 'Criando…' : 'Criar processo'}</UiButton>
                        <UiButton type="button" variant="ghost" onClick={() => navigate(RoutesPaths.PurchasingList)}>Cancelar</UiButton>
                    </div>
                </form>
            </section>
        </div>
    )
}
